import { useState, FormEvent, useEffect, useRef, type ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { rememberEngineerSchools, useAuthStore } from '@/store'
import { DEFAULT_STITUFY_LOGO_DARK, DEFAULT_STITUFY_LOGO_LIGHT } from '@/branding/defaults'
import { isPhoneDevice } from '@/utils/talkWelt'
import { TrimmedLogo } from '@/components/layout/TrimmedLogo'
import { getPublicSystemBranding, poweredByHref, SYSTEM_BRANDING_PUBLIC_KEY } from '@/services/api/systemBranding'
import { resolvePostLoginPath, storePendingLoginNext } from '@/utils/authRedirect'
import { clearPendingLoginCode, readPendingLoginCode, storePendingLoginCode } from '@/utils/pendingLoginCode'
import { getAccessToken, hasStoredSession } from '@/utils/tokenStorage'
import { isStandaloneDisplay, webAuthnAvailable } from '@/utils/pwaDisplay'
import { authApi } from '@/services/api'
import { getPasskey, isWebAuthnCancel } from '@/utils/webauthn'
import { clearPasskeyEnrolled, markPasskeyEnrolled, shouldAutoUnlock } from '@/utils/deviceUnlock'

const RESEND_COOLDOWN_SECONDS = 30

function LoginBrand({ dark }: { dark: boolean }) {
  const { data: systemBranding } = useQuery({
    queryKey: SYSTEM_BRANDING_PUBLIC_KEY,
    queryFn: getPublicSystemBranding,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })
  const systemLogo = dark
    ? DEFAULT_STITUFY_LOGO_DARK
    : (systemBranding?.email_logo_png ||
      systemBranding?.email_logo_svg ||
      DEFAULT_STITUFY_LOGO_LIGHT)
  const href = poweredByHref(systemBranding?.url)
  return (
    <div className="flex justify-center">
      <a href={href} target="_blank" rel="noopener noreferrer">
        <TrimmedLogo
          src={systemLogo}
          alt="Stitufy"
          variant={dark ? 'dark' : 'light'}
          className="h-32 sm:h-40 max-w-[420px] w-auto object-contain"
        />
      </a>
    </div>
  )
}

function LoginShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  const dark = isPhoneDevice()
  return (
    <div className={`min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${dark ? 'bg-[#081018] text-slate-100' : 'bg-gray-50'}`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <LoginBrand dark={dark} />
          <h2 className={`mt-6 text-center text-3xl font-extrabold ${dark ? 'text-slate-100' : 'text-gray-900'}`}>
            {t('login_title')}
          </h2>
          <p className={`mt-2 text-center text-sm ${dark ? 'text-slate-400' : 'text-gray-600'}`}>
            {t('login_subtitle')}
          </p>
        </div>
        {children}
      </div>
    </div>
  )
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(() => readPendingLoginCode() ?? '')
  const [submitted, setSubmitted] = useState(() => readPendingLoginCode() != null)
  const [code, setCode] = useState('')
  const [faceIdError, setFaceIdError] = useState<string | null>(null)

  const [cooldown, setCooldown] = useState(0)
  const { login, verifyCode, completeSession, isLoginSubmitting, error, clearError, fetchUser, isAuthenticated, user } = useAuthStore()
  const finishingRef = useRef(false)
  const autoFaceIdRef = useRef(false)
  const faceIdAction = useRef<(preset?: Record<string, unknown>) => Promise<void>>(async () => {})
  const authOptionsRef = useRef<Record<string, unknown> | null>(null)
  const [passkeyReady, setPasskeyReady] = useState(false)
  const standalone = isStandaloneDisplay()
  const phone = isPhoneDevice()
  const showCode = standalone || phone
  const showPasskey = webAuthnAvailable()

  useEffect(() => {
    const next = searchParams.get('next')
    if (next) storePendingLoginNext(next)
  }, [searchParams])

  useEffect(() => {
    if (finishingRef.current) return
    if (isAuthenticated && user && getAccessToken()) {
      if (rememberEngineerSchools(user) || (user.needs_school_selection && !user.school_id)) {
        navigate('/engineer/schools', { replace: true })
        return
      }
      navigate(resolvePostLoginPath(user.role), { replace: true })
      return
    }
    if (hasStoredSession() || isAuthenticated) void fetchUser()
  }, [isAuthenticated, user, fetchUser, navigate])

  useEffect(() => {
    if (cooldown <= 0) return
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [cooldown])

  useEffect(() => {
    const onStorage = () => {
      if (hasStoredSession()) void fetchUser()
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [fetchUser])

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    clearError()
    try {
      await login(email)
      storePendingLoginCode(email)
      setSubmitted(true)
      setCooldown(RESEND_COOLDOWN_SECONDS)
    } catch {
      // handled by auth store
    }
  }

  async function handleCodeSubmit(e: FormEvent) {
    e.preventDefault()
    clearError()
    finishingRef.current = true
    try {
      const response = await verifyCode(email, code)
      clearPendingLoginCode()
      if (rememberEngineerSchools(response)) {
        navigate('/engineer/schools', { replace: true })
        return
      }
      navigate(resolvePostLoginPath(useAuthStore.getState().user?.role), { replace: true })
    } catch {
      finishingRef.current = false
    }
  }

  async function handleFaceId(preset?: Record<string, unknown>) {
    const cached = preset ?? authOptionsRef.current
    if (!cached) return
    if (!preset) {
      authOptionsRef.current = null
      setPasskeyReady(false)
    }
    const pendingCred = getPasskey(cached)
    setFaceIdError(null)
    clearError()
    try {
      const credential = await pendingCred
      const response = await authApi.webauthnAuthenticate(credential, window.location.origin)
      finishingRef.current = true
      markPasskeyEnrolled()
      await completeSession(response, { enroll: false })
      if (rememberEngineerSchools(response)) {
        navigate('/engineer/schools', { replace: true })
        return
      }
      navigate(resolvePostLoginPath(response.user.role), { replace: true })
    } catch (e) {
      finishingRef.current = false
      if (!preset && showPasskey) {
        void authApi.webauthnAuthenticateOptions().then((options) => {
          authOptionsRef.current = options
          setPasskeyReady(true)
        }).catch(() => {})
      }
      if (isWebAuthnCancel(e)) return
      const msg = e instanceof Error ? e.message : ''
      const missing = /kein face id|kein passkey|nicht gefunden|not found/i.test(msg)
      if (missing) clearPasskeyEnrolled()
      setFaceIdError(t(missing
        ? (phone ? 'login_faceid_missing' : 'login_passkey_missing')
        : (phone ? 'login_faceid_failed' : 'login_passkey_failed')))
    }
  }

  faceIdAction.current = handleFaceId

  useEffect(() => {
    if (!showPasskey || submitted) return
    let cancelled = false
    const load = () => {
      void authApi.webauthnAuthenticateOptions().then((options) => {
        if (cancelled) return
        authOptionsRef.current = options
        setPasskeyReady(true)
      }).catch(() => {
        if (!cancelled) setPasskeyReady(false)
      })
    }
    load()
    const timer = window.setInterval(load, 4 * 60 * 1000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [showPasskey, submitted])

  useEffect(() => {
    if (submitted || isAuthenticated || autoFaceIdRef.current) return
    let cancelled = false
    void (async () => {
      if (!(await shouldAutoUnlock())) return
      const options = await authApi.webauthnAuthenticateOptions()
      if (cancelled || autoFaceIdRef.current) return
      autoFaceIdRef.current = true
      await faceIdAction.current(options)
    })()
    return () => {
      cancelled = true
    }
  }, [submitted, isAuthenticated])

  if (submitted) {
    return (
      <LoginShell>
        <div className="mt-8 text-center">
          <div className={`mb-4 text-lg ${isPhoneDevice() ? 'text-emerald-300' : 'text-green-600'}`}>
            {t('login_success_sent')}
          </div>
          <p className={`mb-4 ${isPhoneDevice() ? 'text-slate-300' : 'text-gray-600'}`}>
            {showCode ? t('login_success_text_pwa') : t('login_success_text')}
          </p>
          {showCode && (
            <form className="mb-6 space-y-4 text-left" onSubmit={handleCodeSubmit}>
              <label htmlFor="login-code" className="sr-only">{t('login_code_label')}</label>
              <input
                id="login-code"
                name="one-time-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^\d]/g, '').slice(0, 6))}
                className={`appearance-none rounded-md relative block w-full px-3 py-2 border text-center tracking-[0.4em] text-lg ${isPhoneDevice() ? 'bg-slate-900 border-slate-600 text-slate-100' : 'border-gray-300 text-gray-900'} focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
                placeholder="000000"
              />
              {error && (
                <div className={`text-sm ${isPhoneDevice() ? 'text-red-400' : 'text-red-600'}`}>{error}</div>
              )}
              <button
                type="submit"
                disabled={isLoginSubmitting || code.length !== 6}
                className="w-full flex justify-center py-2 px-4 rounded-md text-white bg-blue-600 hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
              >
                {t('login')}
              </button>
            </form>
          )}
          <div className={`text-sm mb-6 ${isPhoneDevice() ? 'text-slate-400' : 'text-gray-500'}`}>
            <p>{t('login_success_valid', { minutes: 30 })}</p>
            <p>{t('login_success_once')}</p>
          </div>
          <div className={`rounded-md p-4 mb-4 border ${isPhoneDevice() ? 'bg-amber-950/60 border-amber-800' : 'bg-yellow-50 border-yellow-200'}`}>
            <p className={`text-sm ${isPhoneDevice() ? 'text-amber-100' : 'text-yellow-800'}`}>
              <strong>{t('login_spam_tip')}</strong>
            </p>
          </div>
          {cooldown > 0 ? (
            <p className={`text-sm ${isPhoneDevice() ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('login_resend_in', { seconds: cooldown })}
            </p>
          ) : (
            <button
              onClick={() => {
                clearPendingLoginCode()
                setSubmitted(false)
              }}
              className={`text-sm font-medium ${isPhoneDevice() ? 'text-sky-300' : 'text-blue-600 hover:text-blue-500'}`}
            >
              {t('login_back')}
            </button>
          )}
        </div>
      </LoginShell>
    )
  }

  const errCls = phone ? 'text-red-400' : 'text-red-600'
  const primaryBtn = 'w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50'
  const secondaryBtn = phone
    ? 'w-full flex justify-center py-2 px-4 border border-slate-600 text-sm font-medium rounded-md text-slate-100 bg-transparent hover:bg-slate-800 disabled:opacity-50'
    : 'w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50'

  return (
    <LoginShell>
      <div className="mt-8 space-y-6">
        {showPasskey && (
          <div className="space-y-3">
            {faceIdError && <div className={`text-sm ${errCls}`}>{faceIdError}</div>}
            <button
              type="button"
              onClick={() => void handleFaceId()}
              disabled={!passkeyReady}
              className={primaryBtn}
            >
              {t(phone ? 'login_faceid_button' : 'login_passkey_button')}
            </button>
            <p className={`text-center text-sm ${phone ? 'text-slate-400' : 'text-gray-500'}`}>
              {t('login_or_email')}
            </p>
          </div>
        )}
        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="email" className="sr-only">
              {t('email')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete={showPasskey ? 'username webauthn' : 'email'}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`appearance-none rounded-md relative block w-full px-3 py-2 border sm:text-sm ${phone ? 'bg-slate-900 border-slate-600 placeholder-slate-500 text-slate-100' : 'border-gray-300 placeholder-gray-500 text-gray-900'} focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
              placeholder={t('email_placeholder')}
            />
          </div>

          {error && (
            <div className={`text-sm ${errCls}`}>{error}</div>
          )}
          {!showPasskey && faceIdError && (
            <div className={`text-sm ${errCls}`}>{faceIdError}</div>
          )}

          <button
            type="submit"
            disabled={isLoginSubmitting}
            className={showPasskey ? secondaryBtn : primaryBtn}
          >
            {isLoginSubmitting ? t('login_sending') : t('login_send_link')}
          </button>
        </form>

        <p className="text-xs text-gray-500 text-center">
          {t('login_security_note')}
        </p>
      </div>
    </LoginShell>
  )
}
